import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import styles from './Import.module.css';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

function ChequeLink({ chequeNum, user }) {
  const [imageUrl, setImageUrl] = useState(null);
  const [showImage, setShowImage] = useState(false);

  const handleClick = async () => {
    try {
      const data = await api.searchCheckImages(user.credential, { checkNumber: chequeNum });
      if (data.length > 0 && data[0].s3Key) {
        const { url } = await api.getPresignedUrl(user.credential, data[0].s3Key);
        setImageUrl(url);
        setShowImage(true);
      }
    } catch (e) { console.error(e); }
  };

  return (
    <>
      <span className={styles.chequeLink} onClick={handleClick}>{chequeNum} 📷</span>
      {showImage && imageUrl && (
        <div className={styles.modal} onClick={() => setShowImage(false)}>
          <img src={imageUrl} alt={`צ'ק ${chequeNum}`} className={styles.modalImg} />
        </div>
      )}
    </>
  );
}

export default function ImportPage({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => {
    return searchParams.get('from') || (() => {
      const d = new Date(); d.setMonth(d.getMonth() - 1);
      return d.toISOString().split('T')[0];
    })();
  });
  const [dateTo, setDateTo] = useState(() => {
    return searchParams.get('to') || new Date().toISOString().split('T')[0];
  });
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Sync state to URL
  useEffect(() => {
    const params = {};
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    if (search) params.q = search;
    setSearchParams(params, { replace: true });
  }, [dateFrom, dateTo, search, setSearchParams]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ dateFrom, dateTo });
      if (search) params.set('search', search);
      const res = await fetch(`${BASE_URL}/api/import/account-activities?${params}`, {
        headers: { 'Authorization': `Bearer ${user.credential}` },
      });
      const data = await res.json();
      setActivities(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [dateFrom, dateTo]); // eslint-disable-line

  const handleSearch = (e) => { e.preventDefault(); load(); };

  const handleUpload = async (file, endpoint) => {
    setUploading(true); setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${BASE_URL}/api/import/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${user.credential}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setUploadResult({ success: true, ...data });
      load();
    } catch (e) {
      setUploadResult({ success: false, error: e.message });
    } finally { setUploading(false); }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('he-IL') : '';
  const formatNum = (n) => n ? n.toLocaleString('he-IL', { minimumFractionDigits: 2 }) : '';

  return (
    <div className={styles.container}>
      <h2>תנועות בנק</h2>

      {/* Upload buttons */}
      <div className={styles.uploadRow}>
        <label className={styles.uploadLabel}>
          📤 העלה עו"ש
          <input type="file" accept=".xls,.xlsx,.html" style={{ display: 'none' }}
            onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0], 'account-activity')} />
        </label>
        <label className={styles.uploadLabel}>
          📤 העלה כרטיס אשראי
          <input type="file" accept=".xls,.xlsx" style={{ display: 'none' }}
            onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0], 'credit-card')} />
        </label>
        {uploading && <span className={styles.uploading}>⏳ מעלה...</span>}
      </div>

      {uploadResult && (
        <div className={uploadResult.success ? styles.success : styles.error}>
          {uploadResult.success ? `✓ יובאו ${uploadResult.imported} תנועות` : `✕ ${uploadResult.error}`}
        </div>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        <label>מתאריך <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></label>
        <label>עד תאריך <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></label>
        <form onSubmit={handleSearch} className={styles.searchRow}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="חיפוש: אסמכתא, סוג תנועה..."
            className={styles.searchInput}
          />
          <button type="submit" className={styles.searchBtn}>חפש</button>
        </form>
      </div>

      {/* Stats */}
      {!loading && activities.length > 0 && (
        <div className={styles.stats}>
          <span>{activities.length} תנועות</span>
          <span>זכות: ₪{formatNum(activities.reduce((s, r) => s + (r.credit || 0), 0))}</span>
          <span>חובה: ₪{formatNum(activities.reduce((s, r) => s + (r.debit || 0), 0))}</span>
        </div>
      )}

      {/* Table */}
      {loading && <p className={styles.empty}>טוען...</p>}
      {!loading && activities.length === 0 && <p className={styles.empty}>אין תנועות בטווח הנבחר.</p>}

      {activities.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>תאריך</th>
                <th>סוג</th>
                <th>זכות</th>
                <th>חובה</th>
                <th>יתרה</th>
                <th>אסמכתא</th>
                <th>צ'ק</th>
              </tr>
            </thead>
            <tbody>
              {activities.map((r) => (
                <tr key={r.id} className={r.credit > 0 ? styles.creditRow : ''}>
                  <td>{formatDate(r.activityDate)}</td>
                  <td>{r.activityType}</td>
                  <td className={styles.credit}>{r.credit > 0 ? formatNum(r.credit) : ''}</td>
                  <td className={styles.debit}>{r.debit > 0 ? formatNum(r.debit) : ''}</td>
                  <td>{r.balance ? formatNum(r.balance) : ''}</td>
                  <td className={styles.ref}>{r.reference || ''}</td>
                  <td>{r.chequeNum > 0 ? <ChequeLink chequeNum={r.chequeNum} user={user} /> : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
